import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { lazy, Suspense } from "react";
import { ThemeProvider } from "next-themes";
import { ThemeMetaSync } from "./components/ThemeMetaSync";
import { LanguageInitializer } from "./components/LanguageInitializer";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Courses from "./pages/Courses";
import { AdminRoute } from "./components/AdminRoute";
import { InstructorRoute } from "./components/InstructorRoute";
import { AuthRoute } from "./components/AuthRoute";
import AppShellLayout from "./layouts/AppShellLayout";
import { useScrollbarVisibility } from "./hooks/useScrollbarVisibility";
import { ScrollToTop } from "./components/ScrollToTop";
import { SiteWidePromotionBanner } from "./components/SiteWidePromotionBanner";
import { PromotionPopup } from "./components/PromotionPopup";
import { MaintenanceGuard } from "./components/MaintenanceGuard";
import { NativeShellGate } from "./components/NativeShellGate";
import { PushNotificationsBootstrap } from "./components/PushNotificationsBootstrap";
import RouteFallback from "./components/RouteFallback";

// Tiny inline component to redirect old plural certificate URLs to the singular form
function CertificateRedirect() {
  const { certificateId } = useParams<{ certificateId: string }>();
  return <Navigate to={`/certificate/${certificateId ?? ""}`} replace />;
}

// Lazy load non-critical routes
const Dashboard = lazy(() => import("./pages/Dashboard"));
const MyCourses = lazy(() => import("./pages/MyCourses"));
const CourseDetail = lazy(() => import("./pages/CourseDetail"));
const CourseViewer = lazy(() => import("./pages/CourseViewer"));
const Certificate = lazy(() => import("./pages/Certificate"));
const Pricing = lazy(() => import("./pages/Pricing"));
const PaymentHistory = lazy(() => import("./pages/PaymentHistory"));
const UserSubscriptionManagement = lazy(() => import("./pages/SubscriptionManagement"));
const Contact = lazy(() => import("./pages/Contact"));
const FAQ = lazy(() => import("./pages/FAQ"));
const About = lazy(() => import("./pages/About"));
const Support = lazy(() => import("./pages/Support"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Gamification = lazy(() => import("./pages/Gamification"));
const LearningAnalytics = lazy(() => import("./pages/LearningAnalytics"));
const MyAnalytics = lazy(() => import("./pages/MyAnalytics"));
const MyPerformance = lazy(() => import("./pages/MyPerformance"));
const Profile = lazy(() => import("./pages/Profile"));
const EditProfile = lazy(() => import("./pages/EditProfile"));
const Settings = lazy(() => import("./pages/Settings"));
const Messages = lazy(() => import("./pages/Messages"));
const AiSettings = lazy(() => import("./pages/AiSettings"));
const Announcements = lazy(() => import("./pages/Announcements"));
const Maintenance = lazy(() => import("./pages/Maintenance"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const CheckoutPix = lazy(() => import("./pages/CheckoutPix"));

// Lazy load admin routes
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const QuizAnalyticsDashboard = lazy(() => import("./pages/admin/QuizAnalyticsDashboard"));
const AiQuizTools = lazy(() => import("./pages/admin/AiQuizTools"));
const UsersManagement = lazy(() => import("./pages/admin/UsersManagement"));
const CoursesManagement = lazy(() => import("./pages/admin/CoursesManagement"));
const CourseEditor = lazy(() => import("./pages/admin/CourseEditor"));
const ActivityTracking = lazy(() => import("./pages/admin/ActivityTracking"));
const Analytics = lazy(() => import("./pages/admin/Analytics"));
const CommunicationsManagement = lazy(() => import("./pages/admin/CommunicationsManagement"));
const BlogManagement = lazy(() => import("./pages/admin/BlogManagement"));
const InstructorsManagement = lazy(() => import("./pages/admin/InstructorsManagement"));
const AdvancedAnalytics = lazy(() => import("./pages/admin/AdvancedAnalytics"));
const FeedbackManagement = lazy(() => import("./pages/admin/FeedbackManagement"));
const PromotionsManagement = lazy(() => import("./pages/admin/PromotionsManagement"));
const DiscountsManagement = lazy(() => import("./pages/admin/DiscountsManagement"));
const SecurityDashboard = lazy(() => import("./pages/admin/SecurityDashboard"));
const SecurityAudit = lazy(() => import("./pages/admin/SecurityAudit"));
const IntegrationTesting = lazy(() => import("./pages/admin/IntegrationTesting"));
const SecurityVulnerabilityTesting = lazy(() => import("./pages/admin/SecurityVulnerabilityTesting"));
const SecurityScanner = lazy(() => import("./pages/admin/SecurityScanner"));
const SecurityCompliance = lazy(() => import("./pages/admin/SecurityCompliance"));
const UserIntelligence = lazy(() => import("./pages/admin/UserIntelligence"));
const ABTestingDashboard = lazy(() => import("./pages/admin/ABTestingDashboard"));
const StudentEnrollments = lazy(() => import("./pages/admin/StudentEnrollments"));
const SubscriptionManagement = lazy(() => import("./pages/admin/SubscriptionManagement"));
const PlatformPlans = lazy(() => import("./pages/admin/PlatformPlans"));
const PaymentOperations = lazy(() => import("./pages/admin/PaymentOperations"));
const PaymentHealthDashboard = lazy(() => import("./pages/admin/PaymentHealthDashboard"));
const AccessManagement = lazy(() => import("./pages/admin/AccessManagement"));
const CustomerSupport = lazy(() => import("./pages/admin/CustomerSupport"));
const RoleManagement = lazy(() => import("./pages/admin/RoleManagement"));
const AdminCertificatesManagement = lazy(() => import("./pages/admin/CertificatesManagement"));
const SilvaCoinsManagement = lazy(() => import("./pages/admin/SilvaCoinsManagement"));
const BadgesManagement = lazy(() => import("./pages/admin/BadgesManagement"));
const AchievementsManagement = lazy(() => import("./pages/admin/AchievementsManagement"));
const ChallengesManagement = lazy(() => import("./pages/admin/ChallengesManagement"));
const GamificationOverview = lazy(() => import("./pages/admin/GamificationOverview"));
const CategoriesManagement = lazy(() => import("./pages/admin/CategoriesManagement"));
const FeatureFlags = lazy(() => import("./pages/admin/FeatureFlags"));
const InvitationsAdmin = lazy(() => import("./pages/admin/Invitations"));
const MarketingCampaigns = lazy(() => import("./pages/admin/MarketingCampaigns"));
const BusinessGrowthDashboard = lazy(() => import("./pages/admin/BusinessGrowthDashboard"));
const AiGovernance = lazy(() => import("./pages/admin/AiGovernance"));
const RuntimeCheck = lazy(() => import("./pages/admin/RuntimeCheck"));

// Lazy load user routes
const Invitations = lazy(() => import("./pages/Invitations"));

// Lazy load public marketing pages
const PartnerCampaign = lazy(() => import("./pages/PartnerCampaign"));

// Lazy load instructor routes
const InstructorDashboard = lazy(() => import("./pages/instructor/InstructorDashboard"));
const FeedbackView = lazy(() => import("./pages/instructor/FeedbackView"));
const AnnouncementsPage = lazy(() => import("./pages/instructor/AnnouncementsPage"));
const CertificatesManagement = lazy(() => import("./pages/instructor/CertificatesManagement"));

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 2 * 60 * 1000, // 2 minutes default
        gcTime: 10 * 60 * 1000, // 10 minutes garbage collection
        refetchOnWindowFocus: false, // Don't refetch on window focus by default
        retry: 1, // Only retry failed requests once
      },
    },
  });

const App = () => {
  useScrollbarVisibility();
  
  return (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange storageKey="aicavite-theme">
    <TooltipProvider>
      <ThemeMetaSync />
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <LanguageInitializer>
        <NativeShellGate />
        <PushNotificationsBootstrap />
        <ScrollToTop />
        <SiteWidePromotionBanner />
        <PromotionPopup />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
          {/* Public routes without maintenance guard */}
          <Route path="/auth" element={<Auth />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/maintenance" element={<Maintenance />} />

          {/* Public marketing routes (no dashboard chrome) */}
          <Route path="/" element={<MaintenanceGuard><Index /></MaintenanceGuard>} />
          <Route path="/partner/:slug" element={<MaintenanceGuard><PartnerCampaign /></MaintenanceGuard>} />
          <Route path="/courses" element={<MaintenanceGuard><Courses /></MaintenanceGuard>} />
          <Route path="/courses/:slug" element={<MaintenanceGuard><CourseDetail /></MaintenanceGuard>} />
          <Route path="/courses/id/:id" element={<MaintenanceGuard><CourseDetail /></MaintenanceGuard>} />
          <Route path="/checkout/pix/:courseId" element={<MaintenanceGuard><AuthRoute><CheckoutPix /></AuthRoute></MaintenanceGuard>} />
          <Route path="/learn/:courseSlug" element={<MaintenanceGuard><AuthRoute><CourseViewer /></AuthRoute></MaintenanceGuard>} />
          <Route path="/learn/id/:courseId" element={<MaintenanceGuard><AuthRoute><CourseViewer /></AuthRoute></MaintenanceGuard>} />
          {/* Public certificate verification page (shareable, no auth required) */}
          <Route path="/certificate/:certificateId" element={<MaintenanceGuard><Certificate /></MaintenanceGuard>} />
          {/* Backwards-compat redirect from old plural route */}
          <Route path="/certificates/:certificateId" element={<CertificateRedirect />} />
          <Route path="/pricing" element={<MaintenanceGuard><Pricing /></MaintenanceGuard>} />
          <Route path="/contact" element={<MaintenanceGuard><Contact /></MaintenanceGuard>} />
          <Route path="/about" element={<MaintenanceGuard><About /></MaintenanceGuard>} />
          <Route path="/blog" element={<MaintenanceGuard><Blog /></MaintenanceGuard>} />
          <Route path="/blog/:slug" element={<MaintenanceGuard><BlogPost /></MaintenanceGuard>} />
          <Route path="/privacy-policy" element={<MaintenanceGuard><PrivacyPolicy /></MaintenanceGuard>} />
          <Route path="/terms-of-service" element={<MaintenanceGuard><TermsOfService /></MaintenanceGuard>} />
          <Route path="/cookie-policy" element={<MaintenanceGuard><CookiePolicy /></MaintenanceGuard>} />

          {/* ==========================================================
              Persistent dashboard shell — sidebar + topbar mount ONCE.
              Only the <Outlet /> swaps when navigating between these
              routes, so no full-page reload flicker.
              ========================================================== */}
          <Route element={<MaintenanceGuard><AppShellLayout /></MaintenanceGuard>}>
            {/* In-shell pages that don't strictly require auth (existing behavior) */}
            <Route path="/faq" element={<FAQ />} />
            <Route path="/support" element={<Support />} />

            {/* Authenticated user routes */}
            <Route path="/dashboard" element={<AuthRoute><Dashboard /></AuthRoute>} />
            <Route path="/my-courses" element={<AuthRoute><MyCourses /></AuthRoute>} />
            <Route path="/announcements" element={<AuthRoute><Announcements /></AuthRoute>} />
            <Route path="/gamification" element={<AuthRoute><Gamification /></AuthRoute>} />
            <Route path="/my-analytics" element={<AuthRoute><MyAnalytics /></AuthRoute>} />
            <Route path="/my-performance" element={<AuthRoute><MyPerformance /></AuthRoute>} />
            <Route path="/profile" element={<AuthRoute><Profile /></AuthRoute>} />
            <Route path="/profile/edit" element={<AuthRoute><EditProfile /></AuthRoute>} />
            <Route path="/settings" element={<AuthRoute><Settings /></AuthRoute>} />
            <Route path="/messages" element={<AuthRoute><Messages /></AuthRoute>} />
            <Route path="/payment-history" element={<AuthRoute><PaymentHistory /></AuthRoute>} />
            <Route path="/subscription-management" element={<AuthRoute><UserSubscriptionManagement /></AuthRoute>} />
            <Route path="/invitations" element={<AuthRoute><Invitations /></AuthRoute>} />

            {/* Instructor / advanced routes */}
            <Route path="/learning-analytics" element={<InstructorRoute><LearningAnalytics /></InstructorRoute>} />
            <Route path="/settings/ai" element={<InstructorRoute><AiSettings /></InstructorRoute>} />
            <Route path="/instructor/dashboard" element={<InstructorRoute><InstructorDashboard /></InstructorRoute>} />
            <Route path="/instructor/feedback" element={<InstructorRoute><FeedbackView /></InstructorRoute>} />
            <Route path="/instructor/announcements" element={<InstructorRoute><AnnouncementsPage /></InstructorRoute>} />
            <Route path="/instructor/certificates" element={<InstructorRoute><CertificatesManagement /></InstructorRoute>} />

            {/* Admin routes */}
            <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
            <Route path="/admin/users" element={<AdminRoute><UsersManagement /></AdminRoute>} />
            <Route path="/admin/courses" element={<AdminRoute><CoursesManagement /></AdminRoute>} />
            <Route path="/admin/courses/new" element={<AdminRoute><CourseEditor /></AdminRoute>} />
            <Route path="/admin/courses/:courseId/edit" element={<AdminRoute><CourseEditor /></AdminRoute>} />
            <Route path="/admin/categories" element={<AdminRoute><CategoriesManagement /></AdminRoute>} />
            <Route path="/admin/enrollments" element={<AdminRoute><StudentEnrollments /></AdminRoute>} />
            <Route path="/admin/activity-tracking" element={<AdminRoute><ActivityTracking /></AdminRoute>} />
            <Route path="/admin/analytics" element={<AdminRoute><Analytics /></AdminRoute>} />
            <Route path="/admin/advanced-analytics" element={<AdminRoute><AdvancedAnalytics /></AdminRoute>} />
            <Route path="/admin/business-growth" element={<AdminRoute><BusinessGrowthDashboard /></AdminRoute>} />
            <Route path="/admin/quiz-analytics" element={<AdminRoute><QuizAnalyticsDashboard /></AdminRoute>} />
            <Route path="/admin/ai-quiz-tools" element={<AdminRoute><AiQuizTools /></AdminRoute>} />
            <Route path="/admin/ai-governance" element={<AdminRoute><AiGovernance /></AdminRoute>} />
            <Route path="/admin/instructors" element={<AdminRoute><InstructorsManagement /></AdminRoute>} />
            <Route path="/admin/feedback" element={<AdminRoute><FeedbackManagement /></AdminRoute>} />
            <Route path="/admin/promotions" element={<AdminRoute><PromotionsManagement /></AdminRoute>} />
            <Route path="/admin/discounts" element={<AdminRoute><DiscountsManagement /></AdminRoute>} />
            <Route path="/admin/subscription-management" element={<AdminRoute><SubscriptionManagement /></AdminRoute>} />
            <Route path="/admin/platform-plans" element={<AdminRoute><PlatformPlans /></AdminRoute>} />
            <Route path="/admin/payment-operations" element={<AdminRoute><PaymentOperations /></AdminRoute>} />
            <Route path="/admin/payment-health" element={<AdminRoute><PaymentHealthDashboard /></AdminRoute>} />
            <Route path="/admin/access-management" element={<AdminRoute><AccessManagement /></AdminRoute>} />
            <Route path="/admin/customer-support" element={<AdminRoute><CustomerSupport /></AdminRoute>} />
            <Route path="/admin/role-management" element={<AdminRoute><RoleManagement /></AdminRoute>} />
            <Route path="/admin/communications" element={<AdminRoute><CommunicationsManagement /></AdminRoute>} />
            <Route path="/admin/blog" element={<AdminRoute><BlogManagement /></AdminRoute>} />
            <Route path="/admin/security" element={<AdminRoute><SecurityDashboard /></AdminRoute>} />
            <Route path="/admin/security-audit" element={<AdminRoute><SecurityAudit /></AdminRoute>} />
            <Route path="/admin/integration-testing" element={<AdminRoute><IntegrationTesting /></AdminRoute>} />
            <Route path="/admin/security-vulnerability-testing" element={<AdminRoute><SecurityVulnerabilityTesting /></AdminRoute>} />
            <Route path="/admin/security-scanner" element={<AdminRoute><SecurityScanner /></AdminRoute>} />
            <Route path="/admin/security-compliance" element={<AdminRoute><SecurityCompliance /></AdminRoute>} />
            <Route path="/admin/user-intelligence" element={<AdminRoute><UserIntelligence /></AdminRoute>} />
            <Route path="/admin/ab-testing" element={<AdminRoute><ABTestingDashboard /></AdminRoute>} />
            <Route path="/admin/certificates" element={<AdminRoute><AdminCertificatesManagement /></AdminRoute>} />
            <Route path="/admin/silva-coins" element={<AdminRoute><SilvaCoinsManagement /></AdminRoute>} />
            <Route path="/admin/gamification" element={<AdminRoute><GamificationOverview /></AdminRoute>} />
            <Route path="/admin/badges" element={<AdminRoute><BadgesManagement /></AdminRoute>} />
            <Route path="/admin/achievements" element={<AdminRoute><AchievementsManagement /></AdminRoute>} />
            <Route path="/admin/challenges" element={<AdminRoute><ChallengesManagement /></AdminRoute>} />
            <Route path="/admin/feature-flags" element={<AdminRoute><FeatureFlags /></AdminRoute>} />
            <Route path="/admin/invitations" element={<AdminRoute><InvitationsAdmin /></AdminRoute>} />
            <Route path="/admin/marketing-campaigns" element={<AdminRoute><MarketingCampaigns /></AdminRoute>} />
            <Route path="/admin/runtime-check" element={<AdminRoute><RuntimeCheck /></AdminRoute>} />
          </Route>

          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<MaintenanceGuard><NotFound /></MaintenanceGuard>} />
        </Routes>
        </Suspense>
        </LanguageInitializer>
      </BrowserRouter>
    </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
  );
};

export default App;
